/**
 * ThemeToggle — light/dark switch for the Settings screen.
 *
 * Reads the active theme from useTheme() and exposes two buttons.
 * Clicking either persists to SecureStore (via the provider) and
 * applies the swap immediately (no reload).
 */

import React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { useTheme } from '../../hooks/useTheme'
import { SegmentedToggle } from '../ui/SegmentedToggle'
import { isQaBuild } from '../../lib/qaBuild'
import { SettingsDisclosureArrow } from './SettingsDisclosureArrow'

const THEME_OPTIONS = [
  { value: 'light' as const, label: 'Light', accessibilityLabel: 'Use light theme' },
  { value: 'dark' as const, label: 'Dark', accessibilityLabel: 'Use dark theme' },
]

/** Quick QA palette access shared by lobby settings and the game menu. */
export function QaThemeToggle() {
  return isQaBuild() ? <ThemeToggle compact /> : null
}

/**
 * `embedded` drops the tile's own card so it can sit as a row inside a grouped
 * settings card (with a divider above it), matching the SettingsRow rows.
 */
export function ThemeToggle({ compact = false, embedded = false }: { compact?: boolean; embedded?: boolean }) {
  const { theme, setTheme } = useTheme()
  const [expanded, setExpanded] = React.useState(false)
  const themeName = theme === 'dark' ? 'Night Ops' : 'Field Manual'
  if (compact) return (
    <View testID="qa-theme-toggle" className="bg-paper border border-olive rounded-xl px-4 py-3 flex-row items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
      <Text className="font-stencil text-ink text-xs uppercase">Theme · QA</Text>
      <SegmentedToggle options={THEME_OPTIONS} value={theme} onChange={setTheme} size={34} minWidth={64} />
    </View>
  )
  return (
    <View testID={embedded ? 'theme-toggle-row' : undefined} className={embedded ? 'border-t border-olive p-4 gap-3' : 'bg-paper rounded-2xl overflow-hidden p-4 gap-3'}>
      <TouchableOpacity onPress={() => setExpanded(value => !value)} accessibilityRole="button"
        accessibilityLabel={`Theme: ${themeName}`} accessibilityState={{ expanded }} aria-expanded={expanded}
        style={{ minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text className="font-stencil text-ink text-sm uppercase flex-1">Theme</Text>
        <Text className="font-typed text-ink-soft text-sm">{theme === 'dark' ? '🌙' : '☀'} {themeName}</Text>
        <SettingsDisclosureArrow expanded={expanded} />
      </TouchableOpacity>
      {expanded && <>
      <View className="flex-row gap-2">
        <TouchableOpacity
          onPress={() => setTheme('dark')}
          accessibilityLabel="Use dark theme"
          accessibilityRole="button"
          className={`flex-1 rounded-md py-3 items-center border-2 ${
            theme === 'dark'
              ? 'bg-blood border-ink'
              : 'bg-paper-up border-olive'
          }`}
        >
          <Text className={`font-stencil text-sm ${theme === 'dark' ? 'text-paper' : 'text-ink-soft'}`}>
            🌙 NIGHT OPS
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setTheme('light')}
          accessibilityLabel="Use light theme"
          accessibilityRole="button"
          className={`flex-1 rounded-md py-3 items-center border-2 ${
            theme === 'light'
              ? 'bg-blood border-ink'
              : 'bg-paper-up border-olive'
          }`}
        >
          <Text className={`font-stencil text-sm ${theme === 'light' ? 'text-paper' : 'text-ink-soft'}`}>
            ☀ FIELD MANUAL
          </Text>
        </TouchableOpacity>
      </View>
      <Text className="font-typed text-[10px] text-ink-soft italic">
        // toggles the entire app palette
      </Text>
      </>}
    </View>
  )
}
